<template>
  <HeaderBar />

  <ButtonBackContainer>
    <ButtonBack :fallback="{ name: 'yieldManagement' }">{{ t('back') }}</ButtonBack>
  </ButtonBackContainer>

  <section class="carvia-container">
    <div class="flex flex-col gap-4">
      <AreaChargePricesForm
        v-if="settings"
        :prices="prices"
        :is-saving="isSavingPrices"
        @save="handleSavePrices"
      />
      <Divider />
      <p>{{ t('description') }}</p>
      <div>
        <CVButton @click="openCreate">{{ t('addArea') }}</CVButton>
      </div>

      <div v-if="isLoading" class="flex justify-center py-5">
        <Spinner />
      </div>
      <p v-else-if="!areaCharges?.length">{{ t('empty') }}</p>

      <div v-else class="flex flex-col gap-2">
        <ListItem
          v-for="areaCharge in areaCharges"
          :key="areaCharge.id"
          @click="openEdit(areaCharge)"
        >
          <ListItemColumn :label="t('name')">{{ areaCharge.name }}</ListItemColumn>
          <ListItemColumn :label="t('price')">{{ priceLabel(areaCharge) }}</ListItemColumn>
          <DeleteButton @delete="handleDelete(areaCharge.id, areaCharge.name)" />
        </ListItem>
      </div>
    </div>
  </section>

  <AreaChargeModal v-model:open="isModalOpen" :area-charge="editingAreaCharge" :prices="prices" />
</template>

<script setup lang="ts">
import HeaderBar from '@/components/headerbar/HeaderBar.vue';
import ButtonBackContainer from '@/components/ButtonBackContainer.vue';
import ButtonBack from '@/components/buttons/ButtonBack.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import Divider from '@/components/Divider.vue';
import AreaChargeModal from '@/views/yield-management/area-charges/AreaChargeModal.vue';
import AreaChargePricesForm from '@/views/yield-management/area-charges/AreaChargePricesForm.vue';
import ListItem from '@/views/yield-management/ui/ListItem.vue';
import ListItemColumn from '@/views/yield-management/ui/ListItemColumn.vue';
import DeleteButton from '@/views/yield-management/ui/DeleteButton.vue';
import Spinner from '@/components/icons/Spinner.vue';
import { useAreaCharges, useDeleteAreaCharge } from '@/queries/use-area-charges.ts';
import {
  useUpdateYieldManagementSettings,
  useYieldManagementSettings,
} from '@/queries/use-yield-management-settings.ts';
import { useUiStore } from '@/stores/ui.store.ts';
import { formatCurrency } from '@/utils/format-numbers.ts';
import { Alert } from '@/services/alert.service';
import type { AreaCharge, AreaChargePriceDto } from '@carvia/ros-client-types';
import { computed, ref, watchEffect } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const uiStore = useUiStore();
const { data: areaCharges, isLoading } = useAreaCharges();
const { data: settings } = useYieldManagementSettings();
const { mutateAsync: updateSettings, isPending: isSavingPrices } =
  useUpdateYieldManagementSettings();
const { mutateAsync: deleteAreaCharge } = useDeleteAreaCharge();

const isModalOpen = ref(false);
const editingAreaCharge = ref<AreaCharge | null>(null);
const prices = computed(() => settings.value?.areaChargePrices ?? []);

watchEffect(() => {
  uiStore.setHeaderTitle(t('title'));
});

const priceLabel = (areaCharge: AreaCharge) => {
  if (areaCharge.customPrice != null) return formatCurrency(areaCharge.customPrice);
  const price = prices.value.find((entry) => entry.id === areaCharge.priceId);
  return price ? `${price.name} · ${formatCurrency(price.amount)}` : t('missingPrice');
};

const handleSavePrices = async (areaChargePrices: AreaChargePriceDto[]) => {
  await updateSettings({ areaChargePrices });
};

const openCreate = () => {
  editingAreaCharge.value = null;
  isModalOpen.value = true;
};

const openEdit = (areaCharge: AreaCharge) => {
  editingAreaCharge.value = areaCharge;
  isModalOpen.value = true;
};

const handleDelete = async (id: string, name: string) => {
  const { isDenied } = await Alert.fire({
    text: t('confirmDelete', { name }),
    icon: 'warning',
    showConfirmButton: false,
    showDenyButton: true,
    showCancelButton: true,
    denyButtonText: t('delete'),
    cancelButtonText: t('cancel'),
  });
  if (!isDenied) return;
  await deleteAreaCharge(id);
};
</script>

<i18n lang="json">
{
  "en": {
    "title": "Area charges",
    "back": "Back",
    "description": "Draw an area and pick one of the prices above. The price is added when a transfer enters or leaves the area. A transfer that stays inside is not charged.",
    "addArea": "Add area",
    "empty": "No areas yet.",
    "name": "Name",
    "price": "Price",
    "delete": "Delete",
    "cancel": "Cancel",
    "confirmDelete": "Delete {name}?",
    "missingPrice": "Price missing"
  },
  "de": {
    "title": "Gebietsaufschläge",
    "back": "Zurück",
    "description": "Zeichne ein Gebiet und wähle einen der Preise oben. Der Aufschlag gilt, wenn ein Transfer in das Gebiet hinein- oder aus ihm herausfährt. Bleibt er im Gebiet, fällt nichts an.",
    "addArea": "Gebiet hinzufügen",
    "empty": "Noch keine Gebiete.",
    "name": "Name",
    "price": "Preis",
    "delete": "Löschen",
    "cancel": "Abbrechen",
    "confirmDelete": "{name} löschen?",
    "missingPrice": "Preis fehlt"
  },
  "es": {
    "title": "Recargos por zona",
    "back": "Volver",
    "description": "Dibuja una zona y elige uno de los precios de arriba. El recargo se aplica cuando un traslado entra o sale de la zona. Si se queda dentro, no se cobra.",
    "addArea": "Añadir zona",
    "empty": "Todavía no hay zonas.",
    "name": "Nombre",
    "price": "Precio",
    "delete": "Eliminar",
    "cancel": "Cancelar",
    "confirmDelete": "¿Eliminar {name}?",
    "missingPrice": "Falta el precio"
  }
}
</i18n>
